import AuthLayout from '@layouts/AuthLayout';
import ProtectedLayout from '@layouts/ProtectedLayout';
import MainAppLayout from '@layouts/MainAppLayout';
import { lazyImportWithRetry } from '@utils/lazyImportWithRetry';
import React, { Suspense, useEffect, type ComponentType } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router';
import Support from '@pages/Support';
import Announcements from '@components/Announcements';
import RouteErrorBoundary from '@components/RouteErrorBoundary';
import { useAppLoaderStore } from '@stores/appLoaderStore';

/**
 * Invisible Suspense fallback. Returns null so it doesn't paint its own
 * loader, and instead raises the 'route' flag on mount + lowers it on unmount
 * so AppLoaderGate's single ``<FullscreenLoader />`` keeps painting across the
 * auth→chunk-download handoff. Mount lifecycle is bound to Suspense's fallback
 * lifecycle: when the child chunk resolves, Suspense unmounts the fallback and
 * the cleanup fires — flag lowered.
 */
const RouteChunkLoading = () => {
  useEffect(() => {
    useAppLoaderStore.getState().raise('route');
    return () => useAppLoaderStore.getState().lower('route');
  }, []);
  return null;
};

const lazyRoute = <TModule extends { default: ComponentType<any> }>(
  importer: () => Promise<TModule>,
) => React.lazy(() => lazyImportWithRetry(importer));

const Home = lazyRoute(() => import('@pages/Home'));
const MasqueradePage = lazyRoute(() => import('@pages/Masquerade'));
const AuthCallbackPage = lazyRoute(() => import('@pages/Auth/AuthCallback'));
const AuthCallbackCognitoPage = lazyRoute(
  () => import('@pages/Auth/AuthCallbackCognito'),
);
const SignupPage = lazyRoute(() => import('@pages/Auth/Signup'));
const ForgotPasswordPage = lazyRoute(() => import('@pages/Auth/ForgotPassword'));
const LoginPage = lazyRoute(() => import('@pages/Auth/Login'));
const ResetPasswordPage = lazyRoute(() => import('@pages/Auth/ResetPassword'));
const ResetTempPasswordPage = lazyRoute(
  () => import('@pages/Auth/ResetTempPassword'),
);
const OnboardingPage = lazyRoute(() => import('@pages/Onboarding'));
const SettingsPage = lazyRoute(() => import('@pages/Settings'));
const PrintReportPage = lazyRoute(() => import('@pages/PrintReport'));
const CampaignTimelinePage = lazyRoute(
  () => import('@components/CampaignTimeline'),
);

// Dev Pages - uncomment when needed
// const NotificationTestPage = React.lazy(() => import('@pages/dev/NotificationTestPage'));

const AppRoutes = () => {
  return (
    <Router>
      <RouteErrorBoundary>
        <Announcements />
        <Suspense fallback={<RouteChunkLoading />}>
          <Routes>
            <Route path="callback" element={<AuthCallbackCognitoPage />} />
            {/* Headless print-mode route: rendered by Playwright (Slack PDF
                pipeline). Bypasses ProtectedLayout — auth is the
                HMAC-signed `token` query param, verified by the backend. */}
            <Route
              path="/print/report/:reportId"
              element={<PrintReportPage />}
            />
            <Route element={<AuthLayout />} path="auth">
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route
                path="reset-temp-password"
                element={<ResetTempPasswordPage />}
              />
            </Route>
            <Route element={<ProtectedLayout />}>
              {/* Routes that bypass MainAppLayout */}
              <Route path="/auth-callback" element={<AuthCallbackPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />

              {/* Routes that use MainAppLayout */}
              <Route element={<MainAppLayout />}>
                <Route path="/" element={<Home />} >
                  <Route path="support" element={<Support />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="masquerade" element={<MasqueradePage />} />
                  {/* Dashboards are browser tabs, not routes — the tab shell
                      (EntityViewer) is their only surface. The internal
                      campaign timeline keeps its route. */}
                  <Route
                    path="campaign-timeline"
                    element={<CampaignTimelinePage />}
                  />
                </Route>
                {/* Dev routes - uncomment when needed */}
                {/* <Route path="/dev/notifications" element={<NotificationTestPage />} /> */}
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </Router>
  );
};

export default AppRoutes;
